import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = [
        "situation",
        "children",
        "parts",
        "yearlyIncomes",
        "rentalIncomeNet",
        "csgDeductible",
        "rfrCell",
        "tmiCell",
        "taxAmountCell",
        "socialContributionsCell",
        "totalTaxAmountCell",
        "finalTaxRateCell",
        "sumSalariesCell",
        "abatementCell",
        "netSalariesCell",
        "rentalNetCell",
        "grossIncomeCell",
        "csgCell",
    ];

    connect() {
        // Supporte à la fois les nouveaux et anciens attributs data- pendant la migration
        this.partsUrl = this.element.dataset.incomeTaxPartsUrl || this.element.dataset.incomeTaxModalPartsUrl;
        this.calcUrl = this.element.dataset.incomeTaxCalcUrl || this.element.dataset.incomeTaxModalCalcUrl;

        // Précision d'arrondi pour les pourcentages (configurable via data-income-tax-percent-digits)
        const percentDigitsAttr = this.element.dataset.incomeTaxPercentDigits;
        this.percentDigits = Number.isFinite(parseInt(percentDigitsAttr, 10)) ? parseInt(percentDigitsAttr, 10) : 0;

        this.partsAbortController = null;
        this.calcAbortController = null;

        this.debouncedUpdateTax = this.debounce(() => this.updateTax(), 350);

        this.bindEvents();
        this.updateParts();
        this.updateTax();
    }

    disconnect() {
        if (this.partsAbortController) this.partsAbortController.abort();
        if (this.calcAbortController) this.calcAbortController.abort();
    }

    bindEvents() {
        const onSituationChange = () => this.updateParts();
        this.situationTarget.addEventListener("change", onSituationChange);
        this.childrenTarget.addEventListener("change", onSituationChange);

        const onIncomeChange = () => this.debouncedUpdateTax();
        [
            this.yearlyIncomesTarget,
            this.partsTarget,
            this.rentalIncomeNetTarget,
            this.csgDeductibleTarget,
        ].forEach((el) => el.addEventListener("input", onIncomeChange));
    }

    async updateParts() {
        if (!this.partsUrl) return;
        const params = new URLSearchParams({
            situation: this.situationTarget.value,
            number_of_children: this.childrenTarget.value,
        });
        const json = await this.fetchJsonWithAbort(`${this.partsUrl}?${params.toString()}`, 'partsAbortController');
        if (json && typeof json.parts === 'number') this.fillParts(json.parts);
        this.updateTax();
    }

    fillParts(value) {
        this.partsTarget.value = Number(value || 0).toFixed(1);
    }

    async updateTax() {
        if (!this.calcUrl) return;
        const params = this.serializeParams();
        const json = await this.fetchJsonWithAbort(`${this.calcUrl}?${params.toString()}`, 'calcAbortController');
        if (!json || typeof json !== 'object') return;
        this.updateResultCells(json);
    }

    serializeParams() {
        const formData = new FormData(this.element);
        const params = new URLSearchParams();
        const keyMap = {
            'yearly_rental_incomes': 'rental_income_net',
            'deductible_csg_amount': 'csg_deductible'
        };
        const numericKeys = new Set([
            'yearly_incomes',
            'rental_income_net', 'csg_deductible'
        ]);
        formData.forEach((rawValue, rawKey) => {
            const baseKeyMatch = rawKey.match(/^tax_profile\[(.+)\]$/);
            const baseKey = baseKeyMatch ? baseKeyMatch[1] : rawKey;
            const mappedKey = keyMap[baseKey] || baseKey;
            const value = numericKeys.has(mappedKey) ? this.normalizeNumber(rawValue) : (rawValue || 0);
            params.set(mappedKey, value);
        });
        return params;
    }

    normalizeNumber(value) {
        const cleaned = (value || '').toString().replace(/[^0-9.,-]/g, '').replace(',', '.');
        return cleaned === '' ? '0' : cleaned;
    }

    formatCurrency(n) {
        const v = parseFloat(n || 0);
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(isNaN(v) ? 0 : v);
    }

    formatPercent(n, digits = null) {
        const v = parseFloat(n || 0) * 100;
        const safe = isNaN(v) ? 0 : v;
        const parsed = parseInt(digits, 10);
        const d = Number.isFinite(parsed) ? parsed : (Number.isFinite(this.percentDigits) ? this.percentDigits : 0);
        return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(safe)}%`;
    }

    save() {
        // Copie des valeurs vers les formulaires (#edit-criterias et/ou #edit-profile-form)
        const criteriasForm = document.getElementById('edit-criterias');
        const profileForm = document.getElementById('edit-profile-form');
        const fromCell = (el) => ((el && el.textContent) ? el.textContent : '').replace(/[^0-9.,-]/g, '').replace(',', '.');
        const params = this.serializeParams();

        const setIn = (form, selector, value, eventName = 'change') => {
            if (!form) return;
            const el = form.querySelector(selector);
            if (!el) return;
            el.value = value;
            el.dispatchEvent(new Event(eventName, { bubbles: true }));
            // Compatibilité avec les deux flux ('input' et 'change')
            const secondary = eventName === 'input' ? 'change' : 'input';
            el.dispatchEvent(new Event(secondary, { bubbles: true }));
            el.dispatchEvent(new CustomEvent('number-format:normalize', { bubbles: true }));
        };

        // Extraire valeurs calculées/affichées
        const yearlyIncomes = this.normalizeNumber(params.get('yearly_incomes'));
        const taxNet = fromCell(this.taxAmountCellTarget);
        const totalTax = fromCell(this.totalTaxAmountCellTarget);
        const tmiVal = parseFloat(this.normalizeNumber(this.tmiCellTarget.textContent || ''));
        const tmiText = Number.isFinite(tmiVal) ? tmiVal.toFixed(1) : "";

        // 1) Mettre à jour le formulaire critères (champ name="profile[...]")
        setIn(criteriasForm, 'input[name="profile[yearly_incomes]"]', yearlyIncomes);
        setIn(criteriasForm, 'input[name="profile[tax_amount]"]', taxNet);
        if (tmiText !== "") {
            setIn(criteriasForm, 'select[name="profile[tax_rate]"]', tmiText);
            setIn(criteriasForm, 'input[name="profile[tax_rate]"]', tmiText);
        }
        setIn(criteriasForm, 'input[name="profile[csg_deductible]"]', this.normalizeNumber(params.get('csg_deductible')));
        setIn(criteriasForm, 'input[name="profile[yearly_rental_incomes]"]', this.normalizeNumber(params.get('rental_income_net')));
        setIn(criteriasForm, 'select[name="profile[marital_situation]"]', this.situationTarget.value);
        setIn(criteriasForm, 'select[name="profile[number_of_children]"]', this.childrenTarget.value);
        setIn(criteriasForm, 'select[name="profile[number_of_parts]"]', this.partsTarget.value);

        // 2) Mettre à jour le formulaire profil (panneau gauche simulator #edit-profile-form)
        setIn(profileForm, '#criterias_yearly_incomes', yearlyIncomes, 'blur');
        setIn(profileForm, '#criterias_yearly_rental_incomes', this.normalizeNumber(params.get('rental_income_net')), 'blur');
        setIn(profileForm, '#criterias_tax_amount', totalTax, 'blur');
        if (tmiText !== "") {
            setIn(profileForm, '#criterias_tax_rate', tmiText, 'blur');
        }
        setIn(profileForm, 'input[name="criterias[marital_situation]"]', this.situationTarget.value);
        setIn(profileForm, 'input[name="criterias[number_of_parts]"]', this.partsTarget.value);
        setIn(profileForm, 'input[name="criterias[number_of_children]"]', this.childrenTarget.value);
        setIn(profileForm, 'input[name="criterias[csg_deductible]"]', this.normalizeNumber(params.get('csg_deductible')));

        // Fermer la modale si présente (compat Bootstrap 4/5)
        const modalEl = document.getElementById('ajax-modal');
        if (modalEl) {
            const evt = document.createEvent('Event');
            evt.initEvent('click', true, true);
            const close = modalEl.querySelector('[data-dismiss="modal"], [data-bs-dismiss="modal"]');
            if (close) close.dispatchEvent(evt);
            else if (window.$) window.$(modalEl).modal('hide');
        }
    }

    // Helpers
    async fetchJsonWithAbort(url, controllerProp) {
        try {
            if (this[controllerProp]) this[controllerProp].abort();
            this[controllerProp] = new AbortController();
            const res = await fetch(url, {
                headers: { "X-Requested-With": "XMLHttpRequest" },
                credentials: "same-origin",
                signal: this[controllerProp].signal,
            });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) {
            if (!(e && e.name === 'AbortError')) console.error(e);
            return null;
        }
    }

    updateResultCells(json) {
        const mapping = [
            { key: 'net_salaries_before_abatement', target: 'sumSalariesCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'abatement', target: 'abatementCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'net_salaries_after_abatement', target: 'netSalariesCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'rental_income_net', target: 'rentalNetCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'gross_global_income', target: 'grossIncomeCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'csg_deductible', target: 'csgCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'taxable_total_income', target: 'rfrCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'tmi_rate', target: 'tmiCellTarget', fmt: (v) => this.formatPercent(v) },
            { key: 'income_tax', target: 'taxAmountCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'social_contributions', target: 'socialContributionsCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'total_tax_amount', target: 'totalTaxAmountCellTarget', fmt: (v) => this.formatCurrency(v) },
            { key: 'final_tax_rate', target: 'finalTaxRateCellTarget', fmt: (v) => this.formatPercent(v, 2) },
        ];
        mapping.forEach(({ key, target, fmt }) => {
            if (json[key] !== undefined && this[target]) {
                this[target].textContent = fmt(json[key]);
            }
        });
    }

    setValueAndDispatch(el, value, eventName = 'change') {
        if (!el) return;
        el.value = value;
        el.dispatchEvent(new Event(eventName, { bubbles: true }));
    }

    debounce(fn, delayMs) {
        let timerId = null;
        return (...args) => {
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(() => fn(...args), delayMs);
        };
    }
}